import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth, API } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Send, Sparkles, Plus, Brain, Wand2, Save } from "lucide-react";
import ScentPyramid from "@/components/ScentPyramid";

export default function AIPerfumer() {
  const { authAxios, token } = useAuth();
  const { tr, lang } = useLang();
  const nav = useNavigate();
  const { conversationId: routeConvId } = useParams();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [conversationId, setConversationId] = useState(routeConvId || null);
  const [pyramid, setPyramid] = useState(null);
  const [pyramidBusy, setPyramidBusy] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (routeConvId) {
      (async () => {
        try {
          const r = await authAxios.get(`/conversations/${routeConvId}`);
          setMessages(r.data.messages);
          setConversationId(routeConvId);
          setPyramid(null);
        } catch (e) { toast.error("Conversation not found"); }
      })();
    } else {
      setMessages([]);
      setConversationId(null);
      setPyramid(null);
    }
    // eslint-disable-next-line
  }, [routeConvId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streamingText, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: msg, created_at: now }]);
    setBusy(true);
    setStreamingText("");

    try {
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: msg, conversation_id: conversationId, language: lang }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";
      let newConvId = conversationId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames separated by blank line
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = frame.split("\n");
          let eventName = "message";
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith("event:")) eventName = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
          }
          const dataStr = dataLines.join("\n");
          if (eventName === "done") {
            newConvId = dataStr.trim() || newConvId;
          } else if (dataStr) {
            if (dataStr.startsWith("[ERROR]")) {
              throw new Error(dataStr);
            }
            assembled += dataStr;
            setStreamingText(assembled);
          }
        }
      }

      // Finalize
      setMessages((m) => [...m, { role: "assistant", content: assembled, created_at: new Date().toISOString() }]);
      setStreamingText("");
      if (!conversationId && newConvId) {
        setConversationId(newConvId);
        window.history.replaceState(null, "", `/ai-perfumer/${newConvId}`);
      }
    } catch (e) {
      toast.error(e?.message || "AI error");
      setStreamingText("");
    } finally {
      setBusy(false);
    }
  };

  const generatePyramid = async () => {
    setPyramidBusy(true);
    try {
      const r = await authAxios.post("/ai/scent-pyramid", {
        conversation_id: conversationId,
        language: lang,
      });
      setPyramid(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI error"); }
    setPyramidBusy(false);
  };

  const saveProfile = async () => {
    if (!pyramid) return;
    setSavingProfile(true);
    try {
      const title = pyramid.direction || pyramid.personality || "AI Fragrance Profile";
      await authAxios.post("/ai-profiles/saved", {
        title,
        personality: pyramid.personality || "",
        direction: pyramid.direction || "",
        families: pyramid.families || [],
        top: pyramid.top || [],
        heart: pyramid.heart || [],
        base: pyramid.base || [],
        interpretation: pyramid.interpretation || "",
        source_conversation_id: conversationId,
        language: lang,
      });
      toast.success(tr("ai_saved"));
    } catch (e) { toast.error("Error"); }
    setSavingProfile(false);
  };

  const startNew = () => {
    setMessages([]); setConversationId(null); setPyramid(null); nav("/ai-perfumer");
  };

  const suggestions = [tr("ai_sugg_1"), tr("ai_sugg_2"), tr("ai_sugg_3"), tr("ai_sugg_4"), tr("ai_sugg_5")];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="pfi-overline inline-flex items-center gap-2"><Brain size={12} /> {tr("ai_page_sub")}</div>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">PERSONA <span className="text-[#e6c687]">AI PERFUMER</span></h1>
          </div>
          <button data-testid="ai-new-conversation-btn" onClick={startNew} className="pfi-btn-outline rounded-full px-4 py-2 text-xs inline-flex items-center gap-2">
            <Plus size={14} /> {tr("ai_new_conversation")}
          </button>
        </div>

        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          {/* Chat column */}
          <div className="lg:col-span-2 pfi-card overflow-hidden flex flex-col min-h-[60vh]">
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-5 max-h-[60vh]">
              {messages.length === 0 && !streamingText && (
                <div className="h-full grid place-items-center">
                  <div className="text-center max-w-xl">
                    <div className="pfi-overline inline-flex items-center gap-2 justify-center"><Sparkles size={12} /> {tr("ai_suggestions_title")}</div>
                    <h3 className="mt-4 font-serif text-xl text-[#f8f8fc]">Begin your consultation</h3>
                    <div className="mt-6 flex flex-wrap gap-2 justify-center">
                      {suggestions.map((s, i) => (
                        <button
                          key={i}
                          data-testid={`ai-suggestion-${i}`}
                          onClick={() => send(s)}
                          className="pfi-btn-outline rounded-full px-4 py-2 text-xs"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <MessageBubble key={i} msg={m} />
              ))}
              {streamingText && (
                <MessageBubble msg={{ role: "assistant", content: streamingText }} streaming />
              )}
              {busy && !streamingText && (
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#e6c687] to-[#d4af37]" />
                  <div className="pfi-glass rounded-2xl px-4 py-3 text-sm text-[#a9a9b8]">
                    <span className="animate-pulse">{tr("ai_thinking")}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-white/5 p-4 bg-[#0a0a0c]/60">
              <div className="text-[11px] text-[#6c6c7a] mb-2 font-mono">{tr("ai_disclaimer")}</div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
                <textarea
                  data-testid="ai-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={tr("ai_input_placeholder")}
                  rows={1}
                  className="flex-1 resize-none rounded-2xl bg-[#181820] border border-white/10 px-4 py-3 text-sm text-[#f8f8fc] outline-none focus:border-[#d4af37]/60 min-h-[48px] max-h-40"
                />
                <button
                  type="submit"
                  data-testid="ai-send-btn"
                  disabled={busy || !input.trim()}
                  className="pfi-btn-gold rounded-full h-12 w-12 inline-flex items-center justify-center disabled:opacity-40"
                  aria-label="Send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          </div>

          {/* AI Scent Direction column */}
          <div className="lg:col-span-1 space-y-4">
            <div className="pfi-card p-6">
              <div className="pfi-overline">{tr("ai_direction_title")}</div>
              <div className="mt-2 font-serif text-xl">AI Scent Direction</div>
              <p className="mt-2 text-xs text-[#a9a9b8]">{tr("ai_direction_from")}</p>
              <button
                data-testid="generate-pyramid-btn"
                onClick={generatePyramid}
                disabled={pyramidBusy || messages.length === 0}
                className="mt-4 pfi-btn-gold rounded-full px-4 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-40"
              >
                <Wand2 size={12} /> {pyramid ? tr("ai_direction_regenerate") : tr("ai_direction_generate")}
              </button>
            </div>

            {pyramidBusy && (
              <div className="pfi-card p-6 text-sm text-[#a9a9b8] animate-pulse">…</div>
            )}

            {pyramid && (
              <>
                <div className="pfi-card p-6 space-y-3">
                  {pyramid.personality && (
                    <div>
                      <div className="pfi-overline">{tr("ai_personality")}</div>
                      <div className="mt-1 font-serif text-lg text-[#f8f8fc]">{pyramid.personality}</div>
                    </div>
                  )}
                  {pyramid.direction && (
                    <div>
                      <div className="pfi-overline">{tr("ai_direction_line")}</div>
                      <div className="mt-1 font-serif text-lg text-[#e6c687]">{pyramid.direction}</div>
                    </div>
                  )}
                  {pyramid.families?.length ? (
                    <div>
                      <div className="pfi-overline">{tr("ai_families_rec")}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {pyramid.families.map((f, i) => (
                          <span key={i} className="rounded-full px-3 py-1 text-xs bg-[#181820] border border-[#d4af37]/30 text-[#e6c687]">{f}</span>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>

                <ScentPyramid
                  top={pyramid.top}
                  heart={pyramid.heart}
                  base={pyramid.base}
                  labels={{ top: tr("ai_pyramid_top"), heart: tr("ai_pyramid_heart"), base: tr("ai_pyramid_base") }}
                />

                {pyramid.interpretation && (
                  <div className="pfi-card p-6">
                    <div className="pfi-overline">{tr("ai_interpretation")}</div>
                    <p className="mt-2 text-sm text-[#c8c8d4] leading-relaxed">{pyramid.interpretation}</p>
                  </div>
                )}

                <div className="pfi-card p-4">
                  <button
                    data-testid="save-ai-profile-btn"
                    onClick={saveProfile}
                    disabled={savingProfile}
                    className="w-full pfi-btn-gold rounded-full px-4 py-2.5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    <Save size={12} /> {tr("ai_save_profile")}
                  </button>
                  <div className="mt-3 text-[11px] font-mono text-[#6c6c7a] text-center">
                    {tr("ai_not_official")}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function MessageBubble({ msg, streaming }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}>
      <div className={`h-7 w-7 rounded-full flex-shrink-0 ${isUser ? "bg-[#181820] border border-white/10" : "bg-gradient-to-br from-[#e6c687] to-[#d4af37]"}`} />
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser ? "bg-[#181820] text-[#f8f8fc]" : "pfi-glass text-[#e6c687]/95"
      }`}>
        {msg.content}
        {streaming ? <span className="ml-1 inline-block w-2 h-4 align-middle bg-[#d4af37] animate-pulse" /> : null}
      </div>
    </div>
  );
}
